import React, { useState, useEffect } from 'react';
import { apiService, Banner } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { Plus, Edit, Trash2, Layout } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

const Banners: React.FC = () => {
  const { showToast } = useToast();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [selectedGalleryFile, setSelectedGalleryFile] = useState<File | null>(null);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const data = await apiService.getBanners();
      setBanners(data || []);
    } catch (error) {
      console.error('Failed to fetch banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGallery = async () => {
    try {
      const data = await apiService.getGallery();
      setGalleryImages(data || []);
    } catch (err) {
      console.error('Failed to fetch gallery:', err);
    }
  };

  const handleGallerySelect = async (imageUrl: string) => {
    try {
      setPreviewUrl(imageUrl);
      // Fetch the image and convert to a file to upload it back
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const filename = imageUrl.split('/').pop() || 'gallery-image.jpg';
      const file = new File([blob], filename, { type: blob.type });
      setSelectedGalleryFile(file);
    } catch (err) {
      console.error('Failed to prepare gallery image for saving:', err);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setSelectedGalleryFile(null); // Reset gallery selection if user picks a manual file
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);

    // If we picked from gallery and didn't upload a new file, append the gallery file
    const fileInput = e.currentTarget.querySelector('input[name="background_image"]') as HTMLInputElement;
    if (selectedGalleryFile && (!fileInput.files || fileInput.files.length === 0)) {
      fd.set('background_image', selectedGalleryFile);
    }

    // Normalize booleans
    if (fd.has('is_active')) {
      const val = fd.get('is_active');
      fd.set('is_active', val === 'on' || val === 'true' ? 'true' : 'false');
    } else {
      fd.append('is_active', 'false');
    }

    try {
      if (editingBanner) {
        await apiService.updateBanner(editingBanner.id, fd);
      } else {
        await apiService.createBanner(fd);
      }

      setShowForm(false);
      setEditingBanner(null);
      setPreviewUrl(null);
      setSelectedGalleryFile(null);
      showToast('Banner saved successfully!', 'success');
      fetchBanners();
    } catch (error) {
      console.error('Failed to save banner:', error);
      showToast('Failed to save banner.', 'error');
    }
  };

  const getBannerImage = (banner: Banner) => {
    if (banner.background_image) return getImageUrl(banner.background_image);
    return null; // Removed suggested images logic
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      try {
        await apiService.deleteBanner(id);
        showToast('Banner deleted successfully!', 'success');
        fetchBanners();
      } catch (error) {
        console.error('Failed to delete banner:', error);
        showToast('Failed to delete banner.', 'error');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Banners</h1>
          <p className="text-gray-600">Manage promotional banners and call-to-action sections</p>
        </div>
        <button
          onClick={() => {
            setEditingBanner(null);
            setPreviewUrl(null);
            setSelectedGalleryFile(null);
            fetchGallery();
            setShowForm(true);
          }}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Banner
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900">
                {editingBanner ? 'Edit Banner' : 'New Banner'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input name="title" defaultValue={editingBanner?.title} required className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                  <input name="subtitle" defaultValue={editingBanner?.subtitle} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select name="banner_type" defaultValue={editingBanner?.banner_type || 'banner1'} className="w-full border border-gray-200 rounded-lg px-4 py-2">
                    <option value="banner1">Banner 1</option>
                    <option value="banner2">Banner 2</option>
                    <option value="promotional">Promotional</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea name="description" defaultValue={editingBanner?.description} rows={3} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">BG Color</label>
                  <input type="color" name="background_color" defaultValue={editingBanner?.background_color || '#f97316'} className="w-full h-10 p-1 border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                  <input type="number" name="order" defaultValue={editingBanner?.order || 0} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Banner Image</label>
                  <div className="flex flex-col md:flex-row gap-4 items-start">
                    <div className="w-full md:w-1/2 aspect-video rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center relative group">
                      {previewUrl || (editingBanner?.background_image && getImageUrl(editingBanner.background_image)) ? (
                        <>
                          <img
                            src={(previewUrl || getImageUrl(editingBanner?.background_image)) || undefined}
                            className="w-full h-full object-cover"
                            alt="Preview"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                        </>
                      ) : (
                        <div className="text-gray-400 flex flex-col items-center gap-1">
                          <Layout className="h-8 w-8 opacity-20" />
                          <span className="text-xs font-medium">No image selected</span>
                        </div>
                      )}
                    </div>
                    <div className="w-full md:w-1/2 space-y-2">
                      <p className="text-xs text-gray-500">
                        Upload a high-quality image for your banner. Recommended size: 1920x1080px.
                      </p>
                      <input
                        type="file"
                        name="background_image"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 transition-colors cursor-pointer"
                        required={!editingBanner}
                      />
                      {editingBanner?.background_image && !previewUrl && (
                        <p className="text-[10px] text-gray-400 truncate">
                          Current: {editingBanner.background_image.split('/').pop()}
                        </p>
                      )}
                    </div>
                  </div>

                  {galleryImages.length > 0 && (
                    <div className="mt-4">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Or Choose from Gallery</label>
                      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                        {galleryImages.map((img, i) => (
                          <div
                            key={i}
                            onClick={() => handleGallerySelect(getImageUrl(img.image) || '')}
                            className={`flex-shrink-0 w-20 h-14 rounded border overflow-hidden cursor-pointer transition-colors ${previewUrl === getImageUrl(img.image) ? 'border-orange-500 ring-2 ring-orange-500/20' : 'border-gray-100 hover:border-orange-300'}`}
                          >
                            <img src={getImageUrl(img.image) || undefined} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1 italic">* Note: selecting from gallery will update the banner image when you save.</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                  <input name="cta_text" defaultValue={editingBanner?.cta_text} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Button URL</label>
                  <input name="cta_link" defaultValue={editingBanner?.cta_link} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <input type="checkbox" name="is_active" id="banner_active" defaultChecked={editingBanner?.is_active !== false} className="h-5 w-5 text-orange-600 rounded" />
                  <label htmlFor="banner_active" className="text-sm font-medium text-gray-700">Display publicly</label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-50">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 font-medium">Cancel</button>
                <button type="submit" className="px-8 py-2 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition">
                  {editingBanner ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {banners.map((banner) => (
          <div key={banner.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden group">
            <div
              className="relative h-40 bg-gray-100"
              style={{ backgroundColor: banner.background_color || '#f3f4f6' }}
            >
              {getBannerImage(banner) ? (
                <img src={getBannerImage(banner) || undefined} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-white/50">
                  <Layout className="h-8 w-8 opacity-40 text-white" />
                  <span className="text-[10px] font-bold mt-1 uppercase tracking-widest text-white/70">No Image</span>
                </div>
              )}
              <div className="absolute inset-0 bg-black/40 flex items-end p-4">
                <h3 className="text-xl font-bold text-white">{banner.title}</h3>
              </div>
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                <button
                  onClick={() => {
                    setEditingBanner(banner);
                    setPreviewUrl(null);
                    setSelectedGalleryFile(null);
                    fetchGallery();
                    setShowForm(true);
                  }}
                  className="p-2 bg-white rounded shadow text-orange-600 hover:bg-orange-50"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(banner.id)} className="p-2 bg-white rounded shadow text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="p-4 flex items-center justify-between">
              <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">{banner.banner_type}</span>
              <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${banner.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {banner.is_active ? 'ACTIVE' : 'INACTIVE'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {banners.length === 0 && (
        <div className="text-center py-24 bg-gray-50 rounded-xl border border-gray-100">
          <Layout className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <p className="text-gray-500">No banners found. Click "Add Banner" to start.</p>
        </div>
      )}
    </div>
  );
};

export default Banners;
